import { useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeContextProvider, useThemeContext } from './contexts/ThemeContext';
import { Box, Tabs, Tab, Typography, Stack } from '@mui/material';
import { LocationOn as LocationIcon } from '@mui/icons-material';
import Dashboard from './pages/Overview/Dashboard';
import SpeciesAnalytics from './pages/SpeciesAnalytics';
import TimePatterns from './pages/TimePatterns';
import Comparisons from './pages/Comparisons';
import SystemHealth from './pages/SystemHealth';
import ThemeSelector from './components/ThemeSelector';

const pages = [
  { id: 'overview', label: '🏠 Overview', component: Dashboard },
  { id: 'species', label: '🦅 Species Analytics', component: SpeciesAnalytics },
  { id: 'time', label: '⏰ Time Patterns', component: TimePatterns },
  { id: 'comparisons', label: '⚖️ Comparisons', component: Comparisons },
  { id: 'health', label: '⚡ System Health', component: SystemHealth }
];

const AppContent = () => {
  const { theme } = useThemeContext();
  const [currentTab, setCurrentTab] = useState(0);
  
  const CurrentComponent = pages[currentTab].component;
  
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };
  
  return (
    <ThemeProvider theme={theme.muiTheme}>
      <CssBaseline />
      <Box sx={{ width: '100%' }}>
        {/* Global Header with Logo, Title, and Theme Selector */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <img 
              src="/kingfisher_glasses_logo.svg" 
              alt="Kingfisher Logo" 
              style={{ 
                width: '64px', 
                height: '64px',
                objectFit: 'contain'
              }}
            />
            <Box>
              <Typography variant="h4" component="h1" fontWeight="bold" color="primary">
                Chirp Charts
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Bird Activity Analytics (version 1.0.0)
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={3} alignItems="center">
            <ThemeSelector />
            <Stack direction="row" spacing={1} alignItems="center">
              <LocationIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary" fontWeight="medium">
                Backyard
              </Typography>
            </Stack>            
          </Stack>
        </Box>

        {/* Navigation Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange} 
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              px: 2,
              '& .MuiTab-root': {
                minWidth: 120,
                fontWeight: 500
              }
            }}
          >
            {pages.map((page, index) => (
              <Tab key={page.id} label={page.label} />
            ))}
          </Tabs>
        </Box>
        
        {/* Page Content */}
        <CurrentComponent />
      </Box>
    </ThemeProvider>
  );
};

function App() {
  return (
    <ThemeContextProvider>
      <AppContent />
    </ThemeContextProvider>
  );
}

export default App;
